import React from 'react';
import { CheckCircle, Package, MapPin, Clock, CreditCard, Home, ChevronRight, Tag } from 'lucide-react';
import { getOrderItemImage } from '../../utils/imageHelpers';

const OrderConfirmationModal = ({
  isOpen,
  orderDetails,
  onViewOrders,
  onContinueShopping,
  onContactSupport,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
}) => {
  if (!isOpen) return null;

  const {
    orderId,
    orderNumber,
    items,
    totalAmount,
    originalAmount,
    couponCode,
    couponDiscount,
    shippingCharge,
    taxAmount,
    deliverySlot,
    paymentMethod,
    deliveryAddress,
    estimatedDelivery
  } = orderDetails || {};

  const deliverySlotLabels = {
    morning: { label: 'Morning', time: '6:00 AM - 8:00 AM', icon: '🌅' },
    afternoon: { label: 'Afternoon', time: '2:00 PM - 4:00 PM', icon: '☀️' },
    evening: { label: 'Evening', time: '6:00 PM - 8:00 PM', icon: '🌆' }
  };

  const paymentMethodLabels = {
    cod: { label: 'Cash on Delivery', icon: '💵' },
    upi: { label: 'UPI Payment', icon: '📱' },
    card: { label: 'Card Payment', icon: '💳' },
    wallet: { label: 'Wallet', icon: '👛' },
    upi_cod: { label: 'Cash on Delivery (UPI)', icon: '📱' }
  };

  const selectedSlot = deliverySlotLabels[deliverySlot] || deliverySlotLabels.morning;
  const selectedPayment = paymentMethodLabels[paymentMethod] || paymentMethodLabels.cod;

  return (
    <div className="min-h-full bg-gray-50">
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-b-3xl">
        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
          <CheckCircle size={48} className="text-green-500" />
        </div>
        <h1 className="text-3xl font-bold text-center mb-2">Order Placed Successfully!</h1>
        <p className="text-green-100 text-center">Thank you for your order</p>
      </div>

      <div className="p-4">
        {/* Order ID */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4 text-center">
          <div className="text-sm text-blue-600 mb-1">Order ID</div>
          <div className="text-2xl font-bold text-blue-800">#{orderNumber || orderId}</div>
          <div className="text-xs text-blue-600 mt-2">
            {new Date().toLocaleDateString('en-IN', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>

        {/* Order Items */}
        <div className="mb-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Order Items ({items?.length || 0})
          </h3>
          <div className="bg-white rounded-xl p-4 space-y-3 border border-gray-200">
            {items?.map((item, index) => (
              <div key={index} className="flex items-center gap-3 justify-between">
                <div className="flex items-center gap-3 flex-1">
                  {getOrderItemImage(item, 'small')}
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                    {(item.weight || item.unit) && (
                      <p className="text-xs text-gray-500">
                        {item.weight ? `${item.weight}` : ''}{item.unit ? ` ${item.unit}` : ''}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    {item.sale_price && item.sale_price < item.price && (
                      <p className="text-xs">
                        <span className="text-red-600 font-semibold">₹{item.sale_price}</span>
                        <span className="text-gray-400 line-through ml-1">₹{item.price}</span>
                      </p>
                    )}
                  </div>
                </div>
                <span className="font-semibold text-gray-800">₹{((item.sale_price && item.sale_price < item.price ? item.sale_price : item.price) * item.quantity)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Details */}
        <div className="space-y-3 mb-4">
          {/* Delivery Slot */}
          <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
            <Clock size={20} className="text-orange-600 mt-1" />
            <div className="flex-1">
              <div className="font-semibold text-gray-800 text-sm mb-1">Delivery Slot</div>
              <div className="text-gray-700 flex items-center gap-2">
                <span className="text-lg">{selectedSlot.icon}</span>
                <span className="font-medium">{selectedSlot.label}</span>
                <span className="text-sm text-gray-500">({selectedSlot.time})</span>
              </div>
            </div>
          </div>

          {/* Delivery Address */}
          {deliveryAddress && (
            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
              <MapPin size={20} className="text-purple-600 mt-1" />
              <div className="flex-1">
                <div className="font-semibold text-gray-800 text-sm mb-1">Delivery Address</div>
                <p className="text-gray-700 text-sm">
                  {deliveryAddress.address || '123 Main Street, Sector 29'}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {deliveryAddress.city || 'Gurugram'}, {deliveryAddress.state || 'Haryana'} - {deliveryAddress.pincode || '122001'}
                </p>
              </div>
            </div>
          )}

          {/* Payment Method */}
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
            <CreditCard size={20} className="text-green-600 mt-1" />
            <div className="flex-1">
              <div className="font-semibold text-gray-800 text-sm mb-1">Payment Method</div>
              <div className="text-gray-700 flex items-center gap-2">
                <span className="text-lg">{selectedPayment.icon}</span>
                <span className="font-medium">{selectedPayment.label}</span>
              </div>
              {paymentMethod === 'upi_cod' && (
                <div className="mt-3 flex flex-col items-center w-full">
                  <div className="w-full flex justify-center">
                    <div
                      className="bg-white p-4 rounded-2xl shadow-lg border-2 border-gray-200 flex justify-center"
                      style={{ maxWidth: 400, width: '100%' }}
                    >
                      <img
                        src={upiQrCode}
                        alt="UPI QR Code"
                        style={{ width: 240, height: 240, display: 'block', borderRadius: 12 }}
                      />
                    </div>
                  </div>
                  {upiId && (
                    <p className="text-sm font-semibold text-gray-800 mt-2 text-center">{upiId}</p>
                  )}
                  <p className="text-sm text-gray-700 mt-1 text-center font-semibold tracking-wide">Scan this QR at delivery to pay</p>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>

        {/* Order Total */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 mb-4 text-white">
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-100">Subtotal</span>
            <span className="font-semibold">₹{originalAmount || totalAmount}</span>
          </div>
          {couponCode && couponDiscount > 0 && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-blue-100 flex items-center gap-1">
                <Tag size={16} />
                Coupon ({couponCode})
              </span>
              <span className="font-semibold text-green-300">-₹{couponDiscount}</span>
            </div>
          )}
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-100">Shipping</span>
            <span className="font-semibold">
              {shippingCharge && shippingCharge > 0 ? (
                `₹${shippingCharge}`
              ) : (
                <span className="text-green-300">FREE</span>
              )}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-100">Tax (GST)</span>
            <span className="font-semibold">₹{taxAmount || 0}</span>
          </div>
          <div className="border-t border-blue-400 pt-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold">Total Paid</span>
              <span className="text-2xl font-bold">₹{totalAmount}</span>
            </div>
          </div>
        </div>

        {/* Estimated Delivery */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4 text-center">
          <div className="text-sm text-yellow-700 mb-1">Estimated Delivery</div>
          <div className="text-lg font-bold text-yellow-800">
            {estimatedDelivery || 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onViewOrders}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
          >
            <Package size={24} />
            Track Your Order
            <ChevronRight size={20} />
          </button>

          <button
            onClick={onContinueShopping}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
          >
            <Home size={24} />
            Continue Shopping
          </button>
        </div>

        {/* Thank You Message */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            We'll send you updates about your order via WhatsApp and Email
          </p>
          <button
            type="button"
            onClick={() => onContactSupport && onContactSupport()}
            className="text-xs text-blue-600 mt-2 hover:text-blue-700 font-semibold"
          >
            Questions? Contact Support
          </button>
        </div>
    </div>
  );
};

export default OrderConfirmationModal;
